import { useEffect, useMemo, useCallback } from 'react';
import { set, unset } from 'lodash';
import { useImmer } from 'use-immer';
import uuid from 'uuid/v1';
import { useEventListener } from 'taboola-ultimate-ui';
import { useEntityType } from 'hooks/useEntityType';
import { gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { APP_EVENT_TYPE, tabId, useAppEventContext } from '../app-events-aggregator';
import { ChatAgentContext } from './components/ChatAgentContext/ChatAgentContext';
import { CHAT_AGENT_ACTION_STATUS } from './config/chatAgentActionStatus';
import { CHAT_AGENT_ACTION_TYPE, CHAT_AGENT_MESSAGE_SOURCE } from './config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT_TYPE } from './config/chatAgentComponentType';
import { useChatAgentContextService } from './hooks/useChatAgentContextService';
import { useChatAgentMessageHandler } from './hooks/useChatAgentMessageHandler';
import { useChatAgentMessageSender } from './hooks/useChatAgentMessageSender';
import { useChatAgentMessagesTracer } from './hooks/useChatAgentMessagesTracer';
import { useChatAgentService } from './hooks/useChatAgentService';
import { useChatAgentWebSocketTracer } from './hooks/useChatAgentWebSocketTracer';
import { useTools } from './hooks/useTools';
import { formatAction, getActionDetails } from './utils/eventActionsUtils';

const CHAT_REPLY_TIMEOUT = 10000;

export const ChatAgentProvider = ({ children }) => {
    const { push: pushAppEvent, register } = useAppEventContext();
    const entityType = useEntityType();
    const {
        isOpened: isChatOpened,
        open: openChat,
        close: closeChat,
        initialize: initializeChat,
        container: chatContainer,
        isReady: isChatReady,
        setReady: setChatReady,
        client,
        sessionId,
        setSessionId,
        chatRenderMode,
        isConnected: isChatConnected,
        isCollapsed,
        collapse: collapseChat,
        expand: expandChat,
        chatAgentWidth,
        agentName,
        setAgentName,
        chatServiceMode,
    } = useChatAgentService();
    const {
        registerAgent,
        unregisterAgent,
        clearWindowList,
        sendResponseToChatAgent,
        registerGrid,
        setCellToEdit,
        registerWizard,
        setWizardStep,
        registerChatTextMessageListener,
        notifyChatTextMessageListeners,
    } = useChatAgentMessageSender({ client });
    const { getContext } = useChatAgentContextService();
    const [agentRecommendations, setAgentRecommendations] = useImmer({});

    const addRecommendation = useCallback(
        (field, value) => {
            setAgentRecommendations(draft => set(draft, `${entityType}.${field}`, value));
        },
        [entityType, setAgentRecommendations]
    );

    const removeRecommendation = useCallback(
        field => {
            setAgentRecommendations(draft => {
                unset(draft, `${entityType}.${field}`);
            });
        },
        [entityType, setAgentRecommendations]
    );

    const sendMessageToChat = useCallback(
        async ({ message, triggerName, shouldPushAppEvent = true }) => {
            const context = await getContext();
            const messageObject = {
                id: uuid(),
                messageId: uuid(),
                componentData: {
                    value: message,
                    componentName: CHAT_AGENT_COMPONENT_TYPE.TEXT,
                    isUserMessage: true,
                },
                flows: [
                    {
                        ...context,
                        formContext: context.formContext || {},
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                data: {
                                    value: message,
                                    source: CHAT_AGENT_MESSAGE_SOURCE.USER,
                                },
                            }),
                        ],
                    },
                ],
                sessionId,
                tabId,
            };

            sendResponseToChatAgent({
                client,
                actions: [
                    formatAction({
                        type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                        data: { value: message, isUserMessage: true },
                        status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    }),
                ],
            });

            if (shouldPushAppEvent) {
                pushAppEvent({
                    triggerName,
                    type: APP_EVENT_TYPE.CHAT_DIALOG_MESSAGE,
                    data: messageObject,
                });
            }
        },
        [getContext, sessionId, sendResponseToChatAgent, client, pushAppEvent]
    );

    const { getTool } = useTools({
        closeChat,
        openChat,
        sendResponseToChatAgent,
        addRecommendation,
        sendMessageToChat,
    });

    const messageHandler = useChatAgentMessageHandler({
        registerAgent,
        unregisterAgent,
        sendResponseToChatAgent,
        setCellToEdit,
        setWizardStep,
        openChat,
        closeChat,
        client,
        setSessionId,
        notifyChatTextMessageListeners,
        getTool,
        setAgentName,
    });

    const openChatWithMessage = useCallback(
        ({ userMessage, ...rest } = {}) => {
            openChat(rest);
            if (userMessage) {
                sendMessageToChat({ message: userMessage, shouldPushAppEvent: false });
            }
        },
        [openChat, sendMessageToChat]
    );

    useEventListener('message', messageHandler);

    const value = useMemo(
        () => ({
            registerAgent,
            unregisterAgent,
            registerGrid,
            registerWizard,
            registerChatTextMessageListener,
            isChatOpened,
            openChat: openChatWithMessage,
            closeChat,
            initializeChat,
            chatContainer,
            clearWindowList,
            isChatReady,
            setChatReady,
            client,

            sessionId,
            chatRenderMode,
            isChatConnected,
            sendResponseToChatAgent,
            sendMessageToChat,
            chatAgentWidth,
            isCollapsed,
            collapseChat,
            expandChat,
            getTool,
            agentName,
            setAgentName,
            chatServiceMode,
            agentRecommendations,
            removeRecommendation,
            addRecommendation,
        }),
        [
            registerAgent,
            unregisterAgent,
            registerGrid,
            registerWizard,
            registerChatTextMessageListener,
            isChatOpened,
            openChatWithMessage,
            closeChat,
            initializeChat,
            chatContainer,
            clearWindowList,
            isChatReady,
            setChatReady,
            client,

            sessionId,
            chatRenderMode,
            isChatConnected,
            sendResponseToChatAgent,
            sendMessageToChat,
            chatAgentWidth,
            isCollapsed,
            collapseChat,
            expandChat,
            getTool,
            agentName,
            setAgentName,
            agentRecommendations,
            removeRecommendation,
            addRecommendation,
            chatServiceMode,
        ]
    );

    useEffect(() => {
        let timer;
        return registerChatTextMessageListener(messageAction => {
            const { data } = getActionDetails(messageAction);
            const source = data?.source;
            if (source === CHAT_AGENT_MESSAGE_SOURCE.USER) {
                clearTimeout(timer);
                timer = setTimeout(() => {
                    gtmTracker.trackError('Chat agent message timeout');
                    pushAppEvent({
                        type: APP_EVENT_TYPE.CHAT_AGENT_MESSAGE_TIMEOUT,
                        data: { source, message: 'Chat agent message timeout' },
                    });
                }, CHAT_REPLY_TIMEOUT);
                return;
            }

            if (source === CHAT_AGENT_MESSAGE_SOURCE.AGENT) {
                clearTimeout(timer);
                return;
            }
        });
    }, [registerChatTextMessageListener, sendResponseToChatAgent, pushAppEvent]);

    useChatAgentMessagesTracer(register);
    useChatAgentWebSocketTracer();

    return <ChatAgentContext.Provider value={value}>{children}</ChatAgentContext.Provider>;
};
